import React from 'react';
import { Check, AlertTriangle, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

const SIZE = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 20,
  xl: 24,
};

// Named imports only -- NOT `import * as LucideIcons` -- so the bundler can
// tree-shake every lucide-react icon this app doesn't use. A wildcard
// import plus a dynamic LucideIcons[name] lookup defeats tree-shaking
// entirely (webpack can't statically know which names are ever accessed),
// which pulled the whole ~150KB icon library into the bundle the first
// time a production component actually rendered an <Icon>. Add a new
// icon here (named import + registry entry) when a call site needs one
// not yet listed -- this file remains the single integration point.
const ICONS = { Check, AlertTriangle, ChevronUp, ChevronDown, ChevronsUpDown };

/**
 * Thin wrapper over lucide-react so call sites reference icons by name
 * (Icon.js's own vocabulary) instead of importing lucide components
 * directly everywhere. Swapping the underlying icon library later only
 * touches this file.
 */
function Icon({
  name,
  size = 'md',
  color = 'currentColor',
  strokeWidth = 1.75,
  className = '',
  ...rest
}) {
  const LucideIcon = ICONS[name];

  if (!LucideIcon) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`Icon: unknown icon name "${name}"`);
    }
    return null;
  }

  const pixelSize = typeof size === 'number' ? size : SIZE[size] || SIZE.md;
  const classes = ['ui-icon', className].filter(Boolean).join(' ');

  return (
    <LucideIcon
      size={pixelSize}
      color={color}
      strokeWidth={strokeWidth}
      className={classes}
      aria-hidden="true"
      focusable="false"
      {...rest}
    />
  );
}

export default Icon;
